/**
 * Browse, from design/canvas/Browse.src.html and Find.src.html: the Find bar (a sentence, parsed to
 * filters and ranked), the filter box (type-ahead, grouped dropdown, updates between keystrokes) and the
 * Recent grid. Composer hand-off arrives with Phase 4; this screen finds the clips.
 */
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import {
  AUDIO_DESCRIBE_SETTLE_MS, AUDIO_NAME_MAX, AUDIO_SELECT_MAX, MUSIC_GENRES, MUSIC_MOODS, SFX_CATEGORIES, SFX_SETTINGS, TYPEAHEAD_DEBOUNCE_MS, WORK_POLL_MS,
  numberedNames, type AudioKind, type AudioRoute, type MusicGenre, type MusicMood, type SfxCategory, type SfxSetting,
} from '@studio/contracts';
import { SentBadge, useComposerActions } from '@/components/composer-actions';
import { Icon, Spark } from '@/components/icons';
import { Pager, pageCount } from '@/components/pager';
import { AudioCardMedia, useAudition } from '@/components/audio-card';
import { AudioTable } from '@/components/audio-table';
import { AudioDetails, type AudioDetailsValue } from '@/components/audio-details';
import { RowMenu, type RowMenuItem } from '@/components/row-menu';
import { TopBar } from '@/components/shell';
import { StarRating, ratingMenuItem } from '@/components/star-rating';
import { TagAssetsModal } from '@/components/tag-assets-modal';
import { AnimateConfirm, animationLabel, type AnimateTarget } from '@/components/animate-still';
import { useRatings } from '@/hooks/use-ratings';
import { fmtTime } from '@/lib/format';
import { AnalyseProgress } from '@/routes/analyse';
import { trpc } from '@/lib/trpc';

/** The universal Analyse link: opens the clip on the Analyse page for snippet adjustments. */
export function AnalyseLink({ assetId }: { assetId: string }) {
  return <Link to="/analyse" search={{ asset: assetId }} className="btn quiet send-compose">Analyse</Link>;
}

/**
 * The universal Send to Composer button. A snippet goes straight to the working draft's next free slot;
 * an asset opens the picker, because a whole clip is rarely what the composer wants.
 */
export function SendToComposer({ snippetId, assetId }: { snippetId?: string; assetId?: string }) {
  const add = useMutation(trpc.compose.addSlot.mutationOptions());
  const [picking, setPicking] = useState(false);
  if (snippetId) {
    return (
      <button type="button" className={`btn quiet send-compose${add.isSuccess ? ' sent' : ''}`} disabled={add.isPending}
        title={add.isError ? add.error.message : 'Send to Composer'}
        onClick={() => add.mutate({ snippetId })}>
        {add.isPending ? '…' : add.isSuccess ? `In slot ${add.data.totalSlots}` : add.isError ? 'Failed' : '→ Compose'}
      </button>
    );
  }
  return (
    <>
      <button type="button" className={`btn quiet send-compose${add.isSuccess ? ' sent' : ''}`} title="Choose snippets to send to the Composer" onClick={() => setPicking(true)}>
        {add.isSuccess ? `Sent ${add.data.added}` : '→ Compose'}
      </button>
      {picking && <SnippetPicker assetId={assetId!} onClose={() => setPicking(false)} onSend={(ids) => { add.mutate(ids.length ? { snippetIds: ids } : { assetId: assetId! }); setPicking(false); }} />}
    </>
  );
}

/** The picker: an asset's kept snippets with ticks, send-all at the top. */
function SnippetPicker({ assetId, onClose, onSend }: { assetId: string; onClose: () => void; onSend: (snippetIds: string[]) => void }) {
  const snippets = useQuery(trpc.search.assetSnippets.queryOptions({ assetId }));
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const toggle = (id: string) => setPicked((p) => {
    const n = new Set(p);
    if (n.has(id)) n.delete(id);
    else n.add(id);
    return n;
  });
  return (
    <>
      <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={onClose} />
      <div className="modal picker-modal" role="dialog" aria-label="Send snippets to the Composer">
        <div className="row between">
          <h2>Send to Composer</h2>
          <button type="button" className="btn quiet" onClick={() => onSend([])}>Send all</button>
        </div>
        <div className="picker-list">
          {snippets.data?.map((s) => (
            <button key={s.id} type="button" className={`picker-row${picked.has(s.id) ? ' on' : ''}`} onClick={() => toggle(s.id)} aria-pressed={picked.has(s.id)}>
              <span className="tick"><i className={picked.has(s.id) ? 'on' : ''}>{picked.has(s.id) && <svg viewBox="0 0 16 16"><path d="m3.5 8.5 3 3 6-7" /></svg>}</i></span>
              {s.posterUrl ? <img className="picker-thumb" src={s.posterUrl} alt="" /> : <span className="picker-thumb empty-poster" />}
              <span className="picker-role">{s.role}</span>
              <span className="sub mono picker-time">{fmtTime(s.inMs, false)} – {fmtTime(s.outMs, false)}</span>
              <span className="sub picker-desc">{s.description ?? ''}</span>
            </button>
          ))}
          {snippets.data?.length === 0 && <span className="sub">No kept snippets; Send all sends the whole clip&apos;s set.</span>}
          {snippets.isPending && <div className="spinner" aria-label="Loading" />}
        </div>
        <div className="row between">
          <button type="button" className="btn quiet" onClick={onClose}>Cancel</button>
          <button type="button" className="btn primary" disabled={picked.size === 0} onClick={() => onSend([...picked])}>Send {picked.size || ''} to Composer</button>
        </div>
      </div>
    </>
  );
}

type ToggleKind = 'video' | 'video_snippets' | 'image' | 'audio' | 'audio_snippets';
const TOGGLES: { key: ToggleKind; label: string }[] = [
  { key: 'video', label: 'Video' },
  { key: 'video_snippets', label: 'Video snippets' },
  { key: 'image', label: 'Photos' },
  { key: 'audio', label: 'Audio' },
  { key: 'audio_snippets', label: 'Audio snippets' },
];
type Tab = 'video' | 'photos' | 'audio' | 'all';
const TAB_PRESETS: Record<Tab, ToggleKind[]> = {
  video: ['video', 'video_snippets'],
  photos: ['image'],
  audio: ['audio', 'audio_snippets'],
  all: ['video', 'video_snippets', 'image', 'audio', 'audio_snippets'],
};
const TAB_LABELS: Record<Tab, string> = { video: 'Video', photos: 'Photos', audio: 'Audio', all: 'All' };
const LENGTH_CAP_S = 60;
/** The property-site range: two thumbs, nothing shorter than the left, nothing longer than the right. */
function LengthRange({ min, max, onChange }: { min: number | null; max: number | null; onChange: (min: number | null, max: number | null) => void }) {
  const lo = min ?? 0;
  const hi = max ?? LENGTH_CAP_S;
  return (
    <div className="field browse-select length-range">
      <span className="sub length-label">{lo === 0 && hi === LENGTH_CAP_S ? 'Length · Any' : `Length · ${lo}–${hi === LENGTH_CAP_S ? '60+' : hi} s`}</span>
      <span className="dual-range">
        <input type="range" min={0} max={LENGTH_CAP_S} value={lo} aria-label="Minimum length in seconds"
          onChange={(e) => {
            const v = Math.min(Number(e.target.value), hi - 1);
            onChange(v === 0 ? null : v, max);
          }} />
        <input type="range" min={0} max={LENGTH_CAP_S} value={hi} aria-label="Maximum length in seconds"
          onChange={(e) => {
            const v = Math.max(Number(e.target.value), lo + 1);
            onChange(min, v === LENGTH_CAP_S ? null : v);
          }} />
      </span>
    </div>
  );
}

/**
 * A video or snippet card: hovering plays the clip strictly from its in-point to its out-point
 * (looping within that range) and floats a four-icon action bar over the bottom of the image:
 * Tag, Analyse, Delete, and Send to Composer on the far right. The duration rides as a pill on
 * the image; the second text row carries the AI's description.
 */
function HoverMediaCard({ card, sent, rating, rateItem, onTag, onQuickTag, onAnalyse, onDelete, onCompose, onTrim, onOpen }: {
  card: { name: string; posterUrl: string | null; proxyUrl: string | null; inMs: number | null; outMs: number | null; durationMs: number | null; description: string | null; creatorName: string | null };
  sent: boolean;
  /** Its star rating, always shown, and the same choice for its menu. */
  rating: ReactNode;
  rateItem: RowMenuItem;
  onTag: () => void;
  /** The "Tag this clip" modal, from the card's three-dot menu. */
  onQuickTag: () => void;
  onAnalyse: () => void;
  onDelete: () => void;
  onCompose: () => void;
  onTrim?: (() => void) | undefined;
  /** When set, the whole thumbnail is the action: one click hands the clip to the caller (the Analyser's pick). */
  onOpen?: (() => void) | undefined;
}) {
  const [hover, setHover] = useState(false);
  const inMs = card.inMs ?? 0;
  const outMs = card.outMs ?? card.durationMs ?? 0;
  return (
    <div className="res res-hover" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <MediaFrame onOpen={onOpen} name={card.name}>
        {hover && card.proxyUrl ? (
          <video className="res-th" src={card.proxyUrl} muted playsInline autoPlay
            onLoadedMetadata={(e) => { e.currentTarget.currentTime = inMs / 1000; void e.currentTarget.play(); }}
            onTimeUpdate={(e) => {
              const v = e.currentTarget;
              if (outMs > inMs && v.currentTime * 1000 >= outMs) v.currentTime = inMs / 1000;
            }} />
        ) : card.posterUrl ? <img className="res-th" src={card.posterUrl} alt="" /> : <span className="res-th empty-poster" />}
        {card.durationMs !== null && <span className="res-time mono">{fmtTime(card.durationMs)} s</span>}
        {sent && <span className="res-sent"><SentBadge /></span>}
      </MediaFrame>
      <div className="res-b">
        <div className="res-n">{card.name}</div>
        <div className="sub res-m">{card.creatorName ? `${card.creatorName} · ` : ''}{card.description ?? ''}</div>
        {rating}
      </div>
      {hover && (
        <div className="res-hover-bar">
          <button type="button" title="Send to Composer" aria-label="Send to Composer" onClick={onCompose}><Icon name="film" /></button>
          <button type="button" title="Tag this clip" aria-label="Tag this clip" onClick={onTag}><Icon name="tag" /></button>
          <button type="button" title="Open in the Analyser" aria-label="Open in the Analyser" onClick={onAnalyse}><Icon name="pulse" /></button>
          {onTrim && <button type="button" title="Adjust start and end points" aria-label="Adjust start and end points" onClick={onTrim}><Icon name="trim" /></button>}
          <span className="res-hover-gap" />
          <button type="button" className="danger" title="Delete" aria-label="Delete" onClick={onDelete}><Icon name="trash" /></button>
          <RowMenu title="Clip actions" items={[
            rateItem,
            null,
            { label: 'Tag this clip…', onClick: onQuickTag },
            { label: 'Open in Tagging', onClick: onTag },
          ]} />
        </div>
      )}
    </div>
  );
}

/**
 * A still's card (Dax, 2026-10-05): its picture, name and description, how its latest animation went, and on
 * hover Tag, Animate and Delete; the three-dot menu carries "Animate this image".
 */
function StillCard({ card, rating, rateItem, onTag, onQuickTag, onAnimate, onDelete }: {
  card: { name: string; posterUrl: string | null; description: string | null; creatorName: string | null; animation?: { status: string; error: string | null } | null | undefined };
  rating: ReactNode;
  rateItem: RowMenuItem;
  onTag: () => void;
  onQuickTag: () => void;
  onAnimate: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const state = animationLabel(card.animation);
  return (
    <div className="res res-hover" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      <div className="res-media">
        {card.posterUrl ? <img className="res-th" src={card.posterUrl} alt="" /> : <span className="res-th empty-poster" />}
        {state && <span className={`res-state chip ${state.tone}`} title={state.title}>{state.text}</span>}
      </div>
      <div className="res-b">
        <div className="res-n">{card.name}</div>
        <div className="sub res-m">{card.creatorName ? `${card.creatorName} · ` : ''}{card.description ?? ''}</div>
        {rating}
      </div>
      {hover && (
        <div className="res-hover-bar">
          <button type="button" title="Animate this image" aria-label="Animate this image" onClick={onAnimate}><Icon name="wand" /></button>
          <button type="button" title="Tag this photo" aria-label="Tag this photo" onClick={onTag}><Icon name="tag" /></button>
          <span className="res-hover-gap" />
          <button type="button" className="danger" title="Delete" aria-label="Delete" onClick={onDelete}><Icon name="trash" /></button>
          <RowMenu title="Photo actions" items={[
            rateItem,
            null,
            { label: 'Animate this image', onClick: onAnimate },
            { label: 'Tag this photo…', onClick: onQuickTag },
            { label: 'Open in Tagging', onClick: onTag },
          ]} />
        </div>
      )}
    </div>
  );
}

/** The card's media area: a plain frame, or one button when a single click is meant to pick the clip. */
function MediaFrame({ onOpen, name, children }: { onOpen?: (() => void) | undefined; name: string; children: React.ReactNode }) {
  return onOpen
    ? <button type="button" className="res-media res-open" onClick={onOpen} aria-label={`Load ${name}`}>{children}</button>
    : <div className="res-media">{children}</div>;
}

/** A dropdown over one fixed list; its empty option clears the filter. */
function FilterSelect<T extends string>({ label, value, options, onChange }: { label: string; value: T | null; options: readonly T[]; onChange: (v: T | null) => void }) {
  return (
    <label className="field browse-select">
      <select value={value ?? ''} aria-label={label} onChange={(e) => onChange((e.target.value || null) as T | null)}>
        <option value="">{label} · Any</option>
        {options.map((o) => <option key={o} value={o}>{o}</option>)}
      </select>
    </label>
  );
}

export function BrowsePage() {
  const navigate = useNavigate();
  const search = useSearch({ from: '/_app/browse' });
  const pickSlot = search.pickSlot;
  const pickTarget = search.pick;
  const pickForAnalyser = pickTarget !== undefined;
  const pickPage = pickTarget === 'tagging' ? { to: '/tagging' as const, label: 'Tagging' } : { to: '/analyse' as const, label: 'Analyser' };
  // An import pack opened from its Upload row arrives as ?pack=: the audio tab, filtered to that pack.
  const [pack, setPack] = useState<string | null>(search.pack ?? null);
  const [tab, setTab] = useState<Tab>(search.tab ?? (search.pack ? 'audio' : pickForAnalyser ? 'video' : 'all'));
  // The default browse: video snippets only, the material the composer actually wants. Arriving from
  // the Analyser's Browse button, whole videos only: that is what the Analyser loads.
  const [toggles, setToggles] = useState<ToggleKind[]>(pickForAnalyser ? ['video'] : search.tab ? TAB_PRESETS[search.tab] : search.pack ? TAB_PRESETS.audio : ['video_snippets']);
  const pickTab = (t: Tab) => {
    setTab(t);
    setToggles(TAB_PRESETS[t]);
  };
  const flip = (k: ToggleKind) => setToggles((t) => (t.includes(k) ? t.filter((x) => x !== k) : [...t, k]));
  const [sort, setSort] = useState<'newest' | 'oldest' | 'creator'>('newest');
  const [orientation, setOrientation] = useState<'any' | 'portrait' | 'square' | 'landscape'>('any');
  const [minLen, setMinLen] = useState<number | null>(null);
  const [maxLen, setMaxLen] = useState<number | null>(null);
  const [sound, setSound] = useState<'any' | 'speech' | 'music' | 'background' | 'asmr' | 'none'>('any');
  // The audio library's routes and their filters (Dax, 2026-09-23; Voiceover 2026-09-29): null shows every route.
  const [audioRouteF, setAudioRouteF] = useState<AudioRoute | null>(null);
  const [voiceArtist, setVoiceArtist] = useState<string | null>(null);
  const [genre, setGenre] = useState<MusicGenre | null>(null);
  const [mood, setMood] = useState<MusicMood | null>(null);
  const [trendingOnly, setTrendingOnly] = useState(false);
  const [sfxCategory, setSfxCategory] = useState<SfxCategory | null>(null);
  const [setting, setSetting] = useState<SfxSetting | null>(null);
  const [findOpen, setFindOpen] = useState(false);
  const [sentence, setSentence] = useState('');
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [ddOpen, setDdOpen] = useState(false);
  const filterRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), TYPEAHEAD_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        filterRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const find = useMutation(trpc.search.find.mutationOptions());
  const typeahead = useQuery(trpc.search.typeahead.queryOptions({ q: debounced }, { enabled: debounced.length >= 1 && !findOpen, placeholderData: (p) => p }));
  const quick = useQuery(trpc.search.quickFilters.queryOptions());
  const stats = useQuery(trpc.search.mediaStats.queryOptions());
  const [page, setPage] = useState(1);
  const gridInput = {
      q: debounced || null, kinds: toggles.length ? toggles : TAB_PRESETS.all, sort, orientation, minSeconds: minLen, maxSeconds: maxLen, sound,
      audio: {
        route: audioRouteF,
        genre: audioRouteF === 'music' ? genre : null,
        mood: audioRouteF === 'music' ? mood : null,
        trending: audioRouteF === 'music' && trendingOnly,
        sfxCategory: audioRouteF === 'sfx' ? sfxCategory : null,
        setting: audioRouteF === 'sfx' ? setting : null,
        artist: audioRouteF === 'voice' ? voiceArtist : null,
        pack,
      },
  };
  // A new search, kind, filter or order starts again from the first page.
  const scope = JSON.stringify(gridInput);
  useEffect(() => setPage(1), [scope]);
  const grid = useQuery(trpc.search.browse.queryOptions(
    { ...gridInput, page },
    // Re-read while a sound on screen is still being measured or named, so each card settles in place.
    { placeholderData: (p) => p, refetchInterval: (q) => (q.state.data?.cards.some((c) => c.busy) ? WORK_POLL_MS : false) },
  ));
  const cards = grid.data?.cards;
  // Every card carries its star rating: a clip, a snippet, or a library sound.
  // A still is rated as the asset it is.
  type CardKind = 'asset' | 'snippet' | 'audio' | 'image';
  const targetOf = (c: { kind: CardKind; id: string }) => ({ kind: c.kind === 'audio' ? 'audio_bed' as const : c.kind === 'image' ? 'asset' as const : c.kind, id: c.id });
  const ratingTargets = useMemo(() => (cards ?? []).map((c) => ({ kind: c.kind === 'audio' ? 'audio_bed' as const : c.kind === 'image' ? 'asset' as const : c.kind, id: c.id })), [cards]);
  const ratings = useRatings(ratingTargets);
  const starsOf = (c: { kind: CardKind; id: string }) => (
    <StarRating rating={ratings.ratingOf(targetOf(c))} onRate={(n) => ratings.rate(targetOf(c), n)} />
  );
  const rateItem = (c: { kind: CardKind; id: string }) => ratingMenuItem(ratings.ratingOf(targetOf(c)), (n) => ratings.rate(targetOf(c), n));
  const total = grid.data?.total ?? 0;
  const pageSize = grid.data?.pageSize ?? 1;
  const pages = pageCount(total, pageSize);
  // A page left past the end (sounds removed meanwhile) steps back to the last one.
  useEffect(() => {
    if (grid.data && page > pages) setPage(pages);
  }, [grid.data, page, pages]);
  // The voice artists the Voiceover route is filtered by: whoever the library credits a voiceover to.
  const beds = useQuery(trpc.compose.beds.queryOptions(undefined, { enabled: audioRouteF === 'voice' }));
  const voiceArtists = [...new Set((beds.data ?? []).filter((b) => b.kind === 'voice' && b.artist).map((b) => b.artist!))].sort((a, b) => a.localeCompare(b));
  const audioWork = useQuery(trpc.search.audioWork.queryOptions(undefined, {
    refetchInterval: (q) => (q.state.data && q.state.data.ready < q.state.data.files ? WORK_POLL_MS : false),
    refetchOnWindowFocus: true,
  }));
  const showingFind = findOpen && (find.data !== undefined || find.isPending || find.isError);
  const composer = useComposerActions();
  const attachAudio = useMutation(trpc.compose.attachSlotAudio.mutationOptions({
    onSuccess: () => void navigate({ to: '/compose' }),
  }));
  const removeBed = useMutation(trpc.compose.removeBed.mutationOptions({ onSuccess: () => grid.refetch() }));
  // One player for the whole grid: a hundred cards must not mean a hundred media elements.
  const { playingId, toggle, warm, stop } = useAudition();
  // Selection, iPhone style: checkboxes appear only once Select is on, so nothing is ticked by accident.
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [note, setNote] = useState<string | null>(null);
  // Listening again rewrites the description, and the name too unless the action said otherwise, so
  // the grid has to catch up once the job lands.
  const describeBeds = useMutation(trpc.compose.describeBeds.mutationOptions({
    onSuccess: (r, vars) => {
      setNote(`Listening to ${r.queued} sample${r.queued > 1 ? 's' : ''}${vars.rename ? ' and renaming' : ''}${r.skipped ? `; ${r.skipped} not this brand's` : ''}…`);
      setPicked(new Set());
      setSelecting(false);
      window.setTimeout(() => void grid.refetch(), AUDIO_DESCRIBE_SETTLE_MS);
    },
    onError: (e) => setNote(e.message),
  }));
  const audioIds = (cards ?? []).filter((a) => a.kind === 'audio').map((a) => a.id).slice(0, AUDIO_SELECT_MAX);
  // A new name without a listen: one sound from its card's pencil, or the selection numbered in grid order.
  const [editingName, setEditingName] = useState<{ id: string; name: string } | null>(null);
  const [renamingMany, setRenamingMany] = useState<string | null>(null);
  const pickedInOrder = [...audioIds.filter((id) => picked.has(id)), ...[...picked].filter((id) => !audioIds.includes(id))];
  const renameOne = useMutation(trpc.compose.updateBed.mutationOptions({ onSuccess: () => grid.refetch(), onError: (e) => setNote(e.message) }));
  const renameBeds = useMutation(trpc.compose.renameBeds.mutationOptions({
    onSuccess: (r) => {
      setNote(`Renamed ${r.renamed}${r.skipped ? `; ${r.skipped} not this brand's` : ''}`);
      setRenamingMany(null);
      setPicked(new Set());
      setSelecting(false);
      void grid.refetch();
    },
    onError: (e) => setNote(e.message),
  }));
  const commitName = () => {
    if (!editingName) return;
    const name = editingName.name.trim();
    const was = cards?.find((a) => a.id === editingName.id)?.name;
    setEditingName(null);
    if (name && name !== was) renameOne.mutate({ bedId: editingName.id, name });
  };
  // The preview numbers from where the brand's sounds under that name leave off, as the rename will.
  const renameStart = useQuery(trpc.compose.renameStart.queryOptions(
    { name: renamingMany?.trim() || '-', bedIds: pickedInOrder },
    { enabled: Boolean(renamingMany?.trim()), placeholderData: keepPreviousData },
  ));
  const manyNames = renamingMany !== null ? numberedNames(renamingMany, pickedInOrder.length, renameStart.data?.after ?? 0) : [];
  const audioShown = audioIds.length;
  const togglePick = (id: string) => setPicked((p) => {
    const next = new Set(p);
    if (next.has(id)) next.delete(id);
    else if (next.size < AUDIO_SELECT_MAX) next.add(id);
    return next;
  });
  // A new search must not leave the last sample playing underneath its results.
  useEffect(() => stop(), [debounced, stop]);
  const [bedEdit, setBedEdit] = useState<{ value: AudioDetailsValue; credit: string | null } | null>(null);
  const [tagging, setTagging] = useState<string | null>(null);
  const [animating, setAnimating] = useState<AnimateTarget | null>(null);
  // On the Audio tab the video filters (shape, sound) mean nothing; the audio routes take their place.
  const audioOnly = toggles.length > 0 && toggles.every((t) => t === 'audio' || t === 'audio_snippets');
  // A photo has no length and no sound: on Photos alone those filters go.
  const photosOnly = toggles.length > 0 && toggles.every((t) => t === 'image');
  const packs = useQuery(trpc.search.packs.queryOptions({ include: pack }, { enabled: audioOnly }));
  // Audio reads as cards or as the table (2026-10-04): the table is where sounds are tagged in bulk.
  const [audioView, setAudioView] = useState<'grid' | 'table'>('grid');
  const tableView = audioOnly && audioView === 'table';
  const removeAsset = useMutation(trpc.library.remove.mutationOptions({ onSuccess: () => grid.refetch() }));
  const excludeSnippet = useMutation(trpc.assets.updateSnippet.mutationOptions({ onSuccess: () => grid.refetch() }));

  const submitFind = (e: FormEvent) => {
    e.preventDefault();
    if (sentence.trim().length >= 3) find.mutate({ query: sentence.trim() });
  };

  const understood = find.data?.understood;
  const chips: string[] = understood ? [
    ...understood.facets.map((f) => `${f.key} · ${f.value}`),
    ...understood.roles.map((r) => `role · ${r}`),
    ...understood.creators.map((c) => `creator · ${c}`),
    ...(understood.unusedDays !== null ? [`unused · ${understood.unusedDays} days`] : []),
    ...(understood.spokenText ? [`says · "${understood.spokenText}"`] : []),
  ] : [];

  return (
    <>
      <TopBar title="Browse">
        <div className="seg upload-tabs">
          {(['video', 'photos', 'audio', 'all'] as const).map((t) => (
            <button key={t} type="button" className={tab === t ? 'on' : ''} onClick={() => pickTab(t)}>{TAB_LABELS[t]}</button>
          ))}
        </div>
        {pickForAnalyser && (
          <div className="right">
            <span className="chip acc">Click a video to load it into {pickPage.label}</span>
            <button type="button" className="btn quiet" onClick={() => void navigate({ to: pickPage.to, search: {} })}>Cancel</button>
          </div>
        )}
        {pickSlot !== undefined && (
          <div className="right">
            <span className="chip acc">Picking audio for slot {pickSlot + 1}</span>
            <button type="button" className="btn quiet" onClick={() => void navigate({ to: '/browse', search: tab === 'all' ? {} : { tab } })}>Cancel</button>
          </div>
        )}
      </TopBar>
      <section className="content browse-content">
        <div className="browse-bars">
          <div className={`card ask-bar filter-bar-left${!findOpen ? ' focus-ring' : ''}`}>
            <svg className="filter-glass" viewBox="0 0 16 16" aria-hidden="true"><circle cx="7" cy="7" r="4.5" /><path d="m10.5 10.5 3 3" /></svg>
            <input ref={filterRef} autoFocus value={q} onFocus={() => setDdOpen(true)} onBlur={() => setTimeout(() => setDdOpen(false), 150)}
              onChange={(e) => { setQ(e.target.value); setDdOpen(true); }} placeholder="Filter by name, tag, creator…" aria-label="Filter clips" />
            <span className="chip key-chip">⌘K</span>
            {ddOpen && !findOpen && (typeahead.data?.length ?? 0) > 0 && (
              <div className="dd">
                {typeahead.data!.map((h) => (
                  <button key={`${h.kind}:${h.id}`} type="button" className="dd-row" onMouseDown={(e) => { e.preventDefault(); setQ(h.kind === 'facet' || h.kind === 'phrase' ? h.label.split(' · ').pop()!.replace(/^"…|…"$/g, '') : h.label); setDdOpen(false); }}>
                    <span className="dd-kind">{h.kind}</span>
                    <span className="dd-label">{h.label}</span>
                    {h.count !== null && <span className="sub dd-n">{h.count}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <form className={`card ask-bar${findOpen ? ' focus-ring' : ''}`} onSubmit={submitFind}>
            <span className="ask-spark" aria-hidden="true"><Spark /></span>
            <input value={sentence} onFocus={() => setFindOpen(true)} onChange={(e) => setSentence(e.target.value)}
              placeholder="Ask in plain English, e.g. spoon drags on chocolate protein not used in 60 days" aria-label="Find clips" />
            <span className="chip key-chip">↵</span>
            {findOpen && <button type="button" className="btn quiet" onClick={() => { setFindOpen(false); find.reset(); }}>Close</button>}
          </form>
        </div>
        <div className="row browse-tools">
          <div className="row kind-checks">
            {TOGGLES.map((k) => {
              const n = stats.data ? { video: stats.data.videos, video_snippets: stats.data.videoSnippets, image: stats.data.images, audio: stats.data.audio, audio_snippets: stats.data.audioSnippets }[k.key] : null;
              return (
                <button key={k.key} type="button" className="tick kind-check" role="checkbox" aria-checked={toggles.includes(k.key)}
                  onClick={() => flip(k.key)}>
                  <i className={toggles.includes(k.key) ? 'on' : ''}>{toggles.includes(k.key) && <svg viewBox="0 0 16 16"><path d="m3.5 8.5 3 3 6-7" /></svg>}</i>
                  {k.label}{n !== null && <span className="sub kind-count">{n}</span>}
                </button>
              );
            })}
            {stats.data && <span className="sub kind-total">{stats.data.total} in all</span>}
          </div>
          <button type="button" className="btn sort-toggle" aria-label={sort === 'oldest' ? 'Oldest first; switch to newest first' : 'Newest first; switch to oldest first'}
            title={sort === 'oldest' ? 'Oldest first' : 'Newest first'}
            onClick={() => setSort(sort === 'oldest' ? 'newest' : 'oldest')}>
            Date
            <svg viewBox="0 0 16 16" aria-hidden="true">
              <path d={sort === 'oldest' ? 'M8 13V3M4.5 6.5 8 3l3.5 3.5' : 'M8 3v10M4.5 9.5 8 13l3.5-3.5'} />
            </svg>
          </button>
          {audioOnly ? (
            <>
              <div className="seg" role="radiogroup" aria-label="Music, sound effects or voiceovers">
                {([null, 'music', 'sfx', 'voice'] as const).map((r) => (
                  <button key={r ?? 'all'} type="button" role="radio" aria-checked={audioRouteF === r} className={audioRouteF === r ? 'on' : ''}
                    onClick={() => setAudioRouteF(r)}>{r === null ? 'All' : r === 'music' ? 'Music' : r === 'sfx' ? 'Sound effects' : 'Voiceover'}</button>
                ))}
              </div>
              {(packs.data?.length ?? 0) > 0 && (
                <label className="field browse-select">
                  <select value={pack ?? ''} aria-label="Import pack" onChange={(e) => setPack(e.target.value || null)}>
                    <option value="">Pack · Any</option>
                    {packs.data?.map((p) => <option key={p.id} value={p.id}>{p.name} ({p.files})</option>)}
                  </select>
                </label>
              )}
              {audioRouteF === 'voice' && <FilterSelect label="Voice" value={voiceArtist} options={voiceArtists} onChange={setVoiceArtist} />}
              {audioRouteF === 'music' && (
                <>
                  <FilterSelect label="Genre" value={genre} options={MUSIC_GENRES} onChange={setGenre} />
                  <FilterSelect label="Mood" value={mood} options={MUSIC_MOODS} onChange={setMood} />
                  <button type="button" className="tick" aria-pressed={trendingOnly} onClick={() => setTrendingOnly(!trendingOnly)}>
                    <i className={trendingOnly ? 'on' : ''}>{trendingOnly && <svg viewBox="0 0 16 16"><path d="m3.5 8.5 3 3 6-7" /></svg>}</i>
                    Trending
                  </button>
                </>
              )}
              {audioRouteF === 'sfx' && (
                <>
                  <FilterSelect label="Category" value={sfxCategory} options={SFX_CATEGORIES} onChange={setSfxCategory} />
                  <FilterSelect label="Setting" value={setting} options={SFX_SETTINGS} onChange={setSetting} />
                </>
              )}
            </>
          ) : (
            <div className="seg fmt-seg" role="radiogroup" aria-label="Format">
              <button type="button" className={orientation === 'any' ? 'on' : ''} title="Any format" onClick={() => setOrientation('any')}>Any</button>
              <button type="button" className={orientation === 'portrait' ? 'on' : ''} title="Upright (9:16)" aria-label="Upright 9 by 16" onClick={() => setOrientation('portrait')}><Icon name="ar-9-16" /></button>
              <button type="button" className={orientation === 'square' ? 'on' : ''} title="Square (1:1)" aria-label="Square 1 by 1" onClick={() => setOrientation('square')}><Icon name="ar-1-1" /></button>
              <button type="button" className={orientation === 'landscape' ? 'on' : ''} title="Wide (16:9)" aria-label="Wide 16 by 9" onClick={() => setOrientation('landscape')}><Icon name="ar-16-9" /></button>
            </div>
          )}
          {!photosOnly && <LengthRange min={minLen} max={maxLen} onChange={(a, b) => { setMinLen(a); setMaxLen(b); }} />}
          {!audioOnly && !photosOnly && (
            <label className="field browse-select sound-select">
              <select value={sound} aria-label="Sound" onChange={(e) => setSound(e.target.value as typeof sound)}>
                <option value="any">Sound · Any</option>
                <option value="speech">Voice</option>
                <option value="music">Music</option>
                <option value="background">Background noise</option>
                <option value="asmr">ASMR</option>
                <option value="none">No audio</option>
              </select>
            </label>
          )}
        </div>
        {quick.data && quick.data.length > 0 && (
          <div className="row browse-quick">
            {quick.data.map((f) => (
              <button key={f.label} type="button" className={`chip${debounced === f.label ? ' acc' : ''}`}
                onClick={() => setQ(q === f.label ? '' : f.label)}>{f.label}</button>
            ))}
          </div>
        )}

        {showingFind ? (
          <>
            {understood && (
              <div className="row browse-understood">
                <span className="label">Understood as</span>
                {chips.map((c) => <span key={c} className="chip acc">{c}</span>)}
                {chips.length === 0 && <span className="sub">no exact filters; ranked by meaning alone</span>}
                <span className="sub browse-count">{find.data!.total} matches · {(find.data!.tookMs / 1000).toFixed(1)} s</span>
              </div>
            )}
            {find.isPending && <div className="empty"><div className="spinner" aria-label="Searching" /></div>}
            {find.isError && <div className="card pad error" role="alert">{find.error.message}</div>}
            {find.data && (
              <div className="res-grid">
                {find.data.hits.map((h) => (
                  <div key={h.snippetId} className="res">
                    {h.posterUrl ? <img className="res-th" src={h.posterUrl} alt="" /> : <span className="res-th empty-poster" />}
                    <div className="res-b">
                      <div className="res-n">{h.creatorName ? `${h.creatorName} · ` : ''}{h.role}</div>
                      <div className="sub res-m">{h.unusedDays !== null ? `Unused ${h.unusedDays} d` : 'Never used'} · {fmtTime(h.outMs - h.inMs)} s · {h.assetName}</div>
                      <progress className="fit" value={Math.max(0, Math.min(1, h.score))} max={1} aria-label="Match strength" />
                      <div className="row res-actions"><SendToComposer snippetId={h.snippetId} /><AnalyseLink assetId={h.assetId} /></div>
                    </div>
                  </div>
                ))}
                {find.data.hits.length === 0 && <div className="empty">Nothing matches. Loosen the sentence or drop a filter.</div>}
              </div>
            )}
            {!find.data && !find.isPending && <div className="empty">Describe the clip you need and press Enter.</div>}
          </>
        ) : (
          <>
            <div className="row browse-understood">
              <span className="label">Matching as you type</span>
              {debounced && <span className="chip acc">{debounced}</span>}
              {audioOnly && (
                <div className="seg" role="radiogroup" aria-label="Cards or table">
                  <button type="button" role="radio" aria-checked={audioView === 'grid'} className={audioView === 'grid' ? 'on' : ''} onClick={() => setAudioView('grid')}>Grid</button>
                  <button type="button" role="radio" aria-checked={audioView === 'table'} className={audioView === 'table' ? 'on' : ''} onClick={() => setAudioView('table')}>Table</button>
                </div>
              )}
              {/* Select first, then tick: checkboxes that are always there get ticked by accident. */}
              {audioShown > 0 && !tableView && (
                <button type="button" className={`btn quiet browse-select${selecting ? ' on' : ''}`}
                  title={selecting ? 'Stop selecting' : 'Select audio to analyse'}
                  onClick={() => { setSelecting(!selecting); setPicked(new Set()); setNote(null); }}>
                  {selecting ? 'Done' : 'Select'}
                </button>
              )}
              {total > 0 && !tableView && <Pager page={page} pageSize={pageSize} total={total} onPage={setPage} />}
            </div>
            {audioShown > 0 && audioWork.data && audioWork.data.ready < audioWork.data.files && (
              <div className="row browse-work" role="status">
                <AnalyseProgress big progress={audioWork.data.ready / audioWork.data.files}
                  stage={`${audioWork.data.measuring ? 'Measuring' : 'Naming'} sounds · ${audioWork.data.ready} of ${audioWork.data.files} ready`} />
              </div>
            )}
            {tableView ? <AudioTable route={audioRouteF} q={debounced || null} pack={pack} onOpenPack={setPack} /> : (<>
            {selecting && (
              <div className="row browse-selection" role="status">
                <span className="label">{picked.size} selected</span>
                <button type="button" className="btn quiet" disabled={picked.size >= audioIds.length}
                  onClick={() => setPicked(new Set(audioIds))}>All {audioIds.length > AUDIO_SELECT_MAX ? `(first ${AUDIO_SELECT_MAX})` : ''}</button>
                <button type="button" className="btn quiet" disabled={!picked.size} onClick={() => setPicked(new Set())}>None</button>
                <button type="button" className="btn" disabled={!picked.size || renameBeds.isPending}
                  title="Give them one name, numbered 01, 02, 03 in the order shown, without listening"
                  onClick={() => setRenamingMany('')}>Rename</button>
                <button type="button" className="btn" disabled={!picked.size || describeBeds.isPending}
                  title="Listen to each one and write a description, keeping the names they have"
                  onClick={() => describeBeds.mutate({ bedIds: [...picked], rename: false })}>Analyse</button>
                <button type="button" className="btn primary" disabled={!picked.size || describeBeds.isPending}
                  title="Listen to each one, then name and describe it from what it sounds like"
                  onClick={() => describeBeds.mutate({ bedIds: [...picked], rename: true })}>Analyse and rename</button>
                {note && <span className="sub">{note}</span>}
              </div>
            )}
            <div className="res-grid">
              {cards?.map((a) => a.kind === 'audio' ? (
                <div key={`${a.kind}:${a.id}`} className={`res edit-host${picked.has(a.id) ? ' res-picked' : ''}`}>
                  {selecting && (
                    <label className="res-pick" title={`Select ${a.name}`}>
                      <input type="checkbox" checked={picked.has(a.id)} onChange={() => togglePick(a.id)} aria-label={`Select ${a.name}`} />
                    </label>
                  )}
                  <AudioCardMedia peaks={a.peaks ?? []} playing={playingId === a.id} name={a.name} busy={a.busy ?? false}
                    onToggle={() => (selecting ? togglePick(a.id) : toggle(a.id, a.audioUrl ?? null))}
                    onWarm={() => warm(a.audioUrl ?? null)} />
                  <div className="res-b">
                    <div className="res-name">
                      {editingName?.id === a.id ? (
                        <input className="res-name-input" autoFocus value={editingName.name} maxLength={AUDIO_NAME_MAX} aria-label={`Rename ${a.name}`}
                          onChange={(e) => setEditingName({ id: a.id, name: e.target.value })}
                          onBlur={commitName}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') commitName();
                            if (e.key === 'Escape') setEditingName(null);
                          }} />
                      ) : (
                        <>
                          <span className="res-n">{a.name}</span>
                          {a.trending && <span className="chip acc audio-trend">Trending</span>}
                          {a.mine && !selecting && (
                            <button type="button" className="name-edit" title="Rename" aria-label={`Rename ${a.name}`}
                              onClick={() => setEditingName({ id: a.id, name: a.name })}>
                              <Icon name="pencil" />
                            </button>
                          )}
                        </>
                      )}
                    </div>
                    <div className="sub res-m" title={a.description ?? undefined}>
                      {a.role === 'sfx' ? 'Effect' : a.role === 'voice' ? 'Voiceover' : 'Music'}{a.durationMs ? ` · ${fmtTime(a.durationMs, false)}` : ''}
                      {(() => {
                        // A voiceover says who and where it was said: its artist and its clip.
                        if (a.role === 'voice') return [a.artist, a.sourceName].filter(Boolean).map((w) => ` · ${w}`).join('');
                        // The route's filing words first: they are what this library is browsed by.
                        const filed = a.role === 'sfx' ? [a.sfxCategory, a.setting] : [...(a.genres ?? []), ...(a.moods ?? [])];
                        const words = filed.filter(Boolean).join(', ');
                        const credit = a.artist ?? (a.publisher ? `from ${a.publisher}` : null);
                        return `${words ? ` · ${words}` : ''}${credit ? ` · ${credit}` : ''}${!words && a.description ? ` · ${a.description}` : ''}`;
                      })()}
                    </div>
                    <div className="row res-actions">
                      {starsOf(a)}
                      {pickSlot !== undefined && (
                        <button type="button" className="btn quiet send-compose" disabled={attachAudio.isPending}
                          onClick={() => attachAudio.mutate({ slotIndex: pickSlot, bedId: a.id })}>
                          {attachAudio.isPending ? '…' : `Use for slot ${pickSlot + 1}`}
                        </button>
                      )}
                      <RowMenu items={[
                        rateItem(a),
                        null,
                        { label: playingId === a.id ? 'Stop' : 'Play sample', onClick: () => toggle(a.id, a.audioUrl ?? null) },
                        ...(pickSlot !== undefined ? [{ label: `Layer on slot ${pickSlot + 1} (A2)`, onClick: () => attachAudio.mutate({ slotIndex: pickSlot, bedId: a.id }) }] : []),
                        null,
                        { label: 'Analyse and rename', onClick: () => describeBeds.mutate({ bedIds: [a.id], rename: true }) },
                        { label: 'Analyse, keep the name', onClick: () => describeBeds.mutate({ bedIds: [a.id], rename: false }) },
                        ...(a.mine ? [{
                          label: 'Tag…',
                          onClick: () => setBedEdit({
                            value: {
                              id: a.id, name: a.name, kind: (a.role ?? 'bed') as AudioKind,
                              genres: (a.genres ?? []) as MusicGenre[], moods: (a.moods ?? []) as MusicMood[],
                              sfxCategory: (a.sfxCategory ?? null) as SfxCategory | null, setting: (a.setting ?? null) as SfxSetting | null,
                              trending: a.trending ?? false, tags: a.tags ?? [], artist: a.artist ?? null,
                            },
                            credit: a.role === 'voice' ? a.sourceName ?? null : a.artist ?? (a.publisher ? `From the ${a.publisher} library` : null),
                          }),
                        }, null, { label: 'Delete this audio', danger: true, onClick: () => removeBed.mutate({ bedId: a.id }) }] : []),
                      ]} />
                    </div>
                  </div>
                </div>
              ) : a.kind === 'image' ? (
                <StillCard key={`${a.kind}:${a.id}`} card={a} rating={starsOf(a)} rateItem={rateItem(a)}
                  onTag={() => void navigate({ to: '/tagging', search: { asset: a.id } })}
                  onQuickTag={() => setTagging(a.id)}
                  onAnimate={() => setAnimating({ id: a.id, name: a.name })}
                  onDelete={() => removeAsset.mutate({ assetIds: [a.id] })} />
              ) : (
                <HoverMediaCard key={`${a.kind}:${a.id}`} card={a} sent={a.kind === 'snippet' && composer.inDraft(a.id)} rating={starsOf(a)} rateItem={rateItem(a)}
                  onTag={() => a.assetId && void navigate({ to: '/tagging', search: { asset: a.assetId } })}
                  onQuickTag={() => a.assetId && setTagging(a.assetId)}
                  onAnalyse={() => a.assetId && void navigate({ to: '/analyse', search: { asset: a.assetId } })}
                  onOpen={pickForAnalyser && a.kind === 'asset' ? () => void navigate({ to: pickPage.to, search: { asset: a.id } }) : undefined}
                  onDelete={() => (a.kind === 'asset' ? removeAsset.mutate({ assetIds: [a.id] }) : excludeSnippet.mutate({ snippetId: a.id, excluded: true }))}
                  onCompose={() => (a.kind === 'asset' ? composer.sendAsset(a.id) : composer.sendSnippet(a.id))}
                  onTrim={a.kind === 'snippet' && a.assetId ? () => void navigate({ to: '/analyse', search: { asset: a.assetId!, snippet: a.id } }) : undefined} />
              ))}
              {cards?.length === 0 && (
                <div className="empty">
                  {toggles.every((t) => t === 'audio' || t === 'audio_snippets')
                    ? 'No audio yet. Drop MP3, WAV or M4A files on the Upload page and they land here.'
                    : photosOnly ? 'No photos yet. Drop JPG, PNG or HEIC files on the Upload page and they land here.'
                      : 'Nothing indexed matches.'}
                </div>
              )}
            </div>
            {total > 0 && <div className="row pager-foot"><Pager page={page} pageSize={pageSize} total={total} onPage={setPage} /></div>}
            </>)}
          </>
        )}
        {tagging && <TagAssetsModal assetIds={[tagging]} onClose={() => setTagging(null)} />}
        {animating && <AnimateConfirm still={animating} onClose={() => setAnimating(null)} />}
        {renamingMany !== null && (
          <>
            <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={() => setRenamingMany(null)} />
            <div className="modal managed-modal" role="dialog" aria-label="Rename the selected sounds">
              <h2>Rename {pickedInOrder.length === 1 ? '1 sound' : `${pickedInOrder.length} sounds`}</h2>
              <label className="field">
                <input autoFocus value={renamingMany} aria-label="New name" maxLength={AUDIO_NAME_MAX} placeholder="Mountain Bird"
                  onChange={(e) => setRenamingMany(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && renamingMany.trim()) renameBeds.mutate({ bedIds: pickedInOrder, name: renamingMany });
                    if (e.key === 'Escape') setRenamingMany(null);
                  }} />
              </label>
              {renamingMany.trim() && <span className="sub">{manyNames.length > 1 ? `${manyNames[0]} to ${manyNames[manyNames.length - 1]}` : manyNames[0]}</span>}
              <div className="row between">
                <button type="button" className="btn quiet" onClick={() => setRenamingMany(null)}>Cancel</button>
                <button type="button" className="btn primary" disabled={renameBeds.isPending || !renamingMany.trim()}
                  onClick={() => renameBeds.mutate({ bedIds: pickedInOrder, name: renamingMany })}>
                  {renameBeds.isPending ? 'Renaming…' : 'Rename'}
                </button>
              </div>
            </div>
          </>
        )}
        {bedEdit && (
          <AudioDetails initial={bedEdit.value} credit={bedEdit.credit} onClose={() => setBedEdit(null)} onSaved={() => void grid.refetch()} />
        )}
      </section>
    </>
  );
}
